import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Card, CardContent } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { Plus, Trash2, Package, Tag, Car } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useFormDraft, DraftRestoredBar } from '../FormDraft';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  'engine', 'transmission', 'brakes', 'suspension', 'electrical', 'body',
  'interior', 'exhaust', 'cooling', 'fuel_system', 'ac_system', 'filters',
  'oils_fluids', 'tires', 'others'
];

const emptyBrand = () => ({
  brand_name: '',
  part_number: '',
  supplier: '',
  supplier_contact: '',
  cost_price: '',
  selling_price: '',
  stock_quantity: '0',
  minimum_stock: '1',
  location: '',
  is_active: true,
});

const MasterPartForm = ({ part, onSuccess, onCancel }) => {
  const isEdit = !!part;
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const [master, setMaster] = useState({
    part_name: part?.part_name || '',
    category: part?.category || 'others',
    description: part?.description || '',
    notes: part?.notes || '',
    hsn_code: part?.hsn_code || '',
    gst_rate: part?.gst_rate != null ? String(part.gst_rate) : '18',
  });

  const [brands, setBrands] = useState(() => {
    if (part?.brands && part.brands.length > 0) {
      return part.brands.map((b) => ({
        ...emptyBrand(),
        ...b,
        cost_price: String(b.cost_price ?? ''),
        selling_price: String(b.selling_price ?? ''),
        stock_quantity: String(b.stock_quantity ?? '0'),
        minimum_stock: String(b.minimum_stock ?? '1'),
      }));
    }
    return [emptyBrand()];
  });

  const [loading, setLoading] = useState(false);

  // Vehicle compatibility (make/model/series) sourced from the master vehicle database
  const [selectedMake, setSelectedMake] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedSeries, setSelectedSeries] = useState('');
  const [fitsAllMakes, setFitsAllMakes] = useState(
    isEdit ? !(part?.compatible_makes && part.compatible_makes.length > 0) : false
  );
  const [fitsAllModels, setFitsAllModels] = useState(
    isEdit ? !!(part?.compatible_makes && part.compatible_makes.length > 0 && !(part?.compatible_models && part.compatible_models.length > 0)) : false
  );
  const [availableMakes, setAvailableMakes] = useState([]);
  const [availableModels, setAvailableModels] = useState([]);
  const [availableSeries, setAvailableSeries] = useState([]);
  const [makesLoading, setMakesLoading] = useState(true);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [seriesLoading, setSeriesLoading] = useState(false);

  // Suppliers sourced from the supplier database
  const [suppliers, setSuppliers] = useState([]);

  // Autosave draft (create only)
  const draftHasContent = !isEdit && (
    master.part_name.trim() !== '' ||
    master.description.trim() !== '' ||
    !!selectedMake ||
    brands.some((b) => (b.brand_name || '').trim() || (b.part_number || '').trim() || b.cost_price || b.selling_price)
  );
  const { draftRestored, clearDraft, dismissBanner } = useFormDraft({
    storageKey: 'draft:master-part-create',
    enabled: !isEdit,
    hasContent: draftHasContent,
    data: { master, brands, selectedMake, selectedModel, selectedSeries },
    onRestore: (d) => {
      if (d.master) setMaster(d.master);
      if (Array.isArray(d.brands) && d.brands.length) setBrands(d.brands);
      const mk = d.selectedMake || '';
      const md = d.selectedModel || '';
      const sr = d.selectedSeries || '';
      if (mk) {
        setSelectedMake(mk);
        axios.get(`${API}/api/vehicle-data/models/${encodeURIComponent(mk)}`)
          .then((r) => setAvailableModels((r.data.models || []).filter(Boolean))).catch(() => {});
        if (md) {
          setSelectedModel(md);
          axios.get(`${API}/api/vehicle-data/series/${encodeURIComponent(mk)}/${encodeURIComponent(md)}`)
            .then((r) => setAvailableSeries((r.data.series || []).filter(Boolean))).catch(() => {});
          if (sr) setSelectedSeries(sr);
        }
      }
    },
  });

  const discardDraft = () => {
    clearDraft();
    setMaster({ part_name: '', category: 'others', description: '', notes: '' });
    setBrands([emptyBrand()]);
    setSelectedMake(''); setSelectedModel(''); setSelectedSeries('');
    setAvailableModels([]); setAvailableSeries([]);
  };

  // Existing brand names for autocomplete
  const [brandNameOptions, setBrandNameOptions] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/parts/brand-names`);
        setBrandNameOptions(res.data.brand_names || []);
      } catch (e) {
        console.error('Failed to load brand names:', e);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const res = await axios.get(`${API}/api/suppliers`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const list = Array.isArray(res.data) ? res.data : (res.data.suppliers || []);
        setSuppliers(list.filter((s) => s && s.name));
      } catch (e) {
        console.error('Failed to load suppliers:', e);
      }
    })();
  }, []);

  const handleSupplierChange = (idx, name) => {
    const match = suppliers.find((s) => s.name === name);
    setBrands((prev) => prev.map((b, i) => (
      i === idx ? { ...b, supplier: name, supplier_contact: match?.phone || b.supplier_contact } : b
    )));
  };

  useEffect(() => {
    (async () => {
      try {
        setMakesLoading(true);
        const res = await axios.get(`${API}/api/vehicle-data/makes`);
        setAvailableMakes((res.data.makes || []).filter(Boolean));
        // Pre-populate cascade when editing an existing part
        const initMake = part?.compatible_makes?.[0] || '';
        const initModel = part?.compatible_models?.[0] || '';
        const initSeries = part?.compatible_series?.[0] || '';
        if (initMake) {
          setSelectedMake(initMake);
          const mRes = await axios.get(`${API}/api/vehicle-data/models/${encodeURIComponent(initMake)}`);
          setAvailableModels((mRes.data.models || []).filter(Boolean));
          if (initModel) {
            setSelectedModel(initModel);
            const sRes = await axios.get(`${API}/api/vehicle-data/series/${encodeURIComponent(initMake)}/${encodeURIComponent(initModel)}`);
            setAvailableSeries((sRes.data.series || []).filter(Boolean));
            if (initSeries) setSelectedSeries(initSeries);
          }
        }
      } catch (e) {
        console.error('Failed to load vehicle makes:', e);
      } finally {
        setMakesLoading(false);
      }
    })();
  }, []);

  const handleMakeChange = async (make) => {
    setSelectedMake(make);
    setSelectedModel('');
    setSelectedSeries('');
    setAvailableModels([]);
    setAvailableSeries([]);
    setFitsAllModels(false);
    if (!make) return;
    try {
      setModelsLoading(true);
      const res = await axios.get(`${API}/api/vehicle-data/models/${encodeURIComponent(make)}`);
      setAvailableModels((res.data.models || []).filter(Boolean));
    } catch (e) {
      console.error('Failed to load models:', e);
    } finally {
      setModelsLoading(false);
    }
  };

  const handleFitsAllMakesChange = (checked) => {
    setFitsAllMakes(checked);
    if (checked) {
      setSelectedMake('');
      setSelectedModel('');
      setSelectedSeries('');
      setFitsAllModels(false);
    }
  };

  const handleFitsAllModelsChange = (checked) => {
    setFitsAllModels(checked);
    if (checked) {
      setSelectedModel('');
      setSelectedSeries('');
    }
  };

  const handleModelChange = async (model) => {
    setSelectedModel(model);
    setSelectedSeries('');
    setAvailableSeries([]);
    if (!selectedMake || !model) return;
    try {
      setSeriesLoading(true);
      const res = await axios.get(`${API}/api/vehicle-data/series/${encodeURIComponent(selectedMake)}/${encodeURIComponent(model)}`);
      setAvailableSeries((res.data.series || []).filter(Boolean));
    } catch (e) {
      console.error('Failed to load series:', e);
    } finally {
      setSeriesLoading(false);
    }
  };

  const handleSeriesChange = (series) => setSelectedSeries(series);

  const updateBrand = (idx, field, value) => {
    setBrands((prev) => prev.map((b, i) => (i === idx ? { ...b, [field]: value } : b)));
  };
  const addBrand = () => setBrands((prev) => [...prev, emptyBrand()]);
  const removeBrand = (idx) => setBrands((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!master.part_name.trim()) {
      toast.error('Master Part Name is required');
      return;
    }
    if (!fitsAllMakes && !selectedMake) {
      toast.error("Vehicle Compatibility is required — select a Make or check 'Fits All Makes'");
      return;
    }
    const cleanBrands = brands.filter((b) => b.brand_name.trim() || b.part_number.trim());
    if (cleanBrands.length === 0) {
      toast.error('Add at least one brand');
      return;
    }
    for (const b of cleanBrands) {
      if (!b.brand_name.trim()) {
        toast.error('Each brand needs a Brand Name');
        return;
      }
    }

    const payloadBrands = cleanBrands.map((b) => ({
      id: b.id,
      brand_name: b.brand_name.trim(),
      part_number: (b.part_number || '').trim(),
      supplier: (b.supplier || '').trim() || null,
      supplier_contact: (b.supplier_contact || '').trim() || null,
      cost_price: parseFloat(b.cost_price) || 0,
      selling_price: parseFloat(b.selling_price) || 0,
      stock_quantity: parseFloat(b.stock_quantity) || 0,
      minimum_stock: parseFloat(b.minimum_stock) || 0,
      location: (b.location || '').trim() || null,
      is_active: b.is_active !== false,
    }));

    const payload = {
      part_name: master.part_name.trim(),
      category: master.category,
      description: master.description || null,
      notes: master.notes || null,
      hsn_code: (master.hsn_code || '').trim() || null,
      gst_rate: parseFloat(master.gst_rate) || 0,
      brands: payloadBrands,
      compatible_makes: (fitsAllMakes || !selectedMake) ? null : [selectedMake],
      compatible_models: (fitsAllMakes || fitsAllModels || !selectedModel) ? null : [selectedModel],
      compatible_series: selectedSeries ? [selectedSeries] : null,
    };

    setLoading(true);
    try {
      const url = isEdit ? `${backendUrl}/api/parts/${part.id}` : `${backendUrl}/api/parts`;
      const method = isEdit ? 'PUT' : 'POST';
      const token = localStorage.getItem('auth_token');
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.text();
        throw new Error(err);
      }
      const savedPart = await res.json();
      clearDraft();
      toast.success(isEdit ? 'Part updated successfully' : 'Master part created successfully');
      onSuccess && onSuccess(savedPart);
    } catch (err) {
      console.error('Save part failed:', err);
      toast.error('Failed to save part');
    } finally {
      setLoading(false);
    }
  };

  const totalStock = brands.reduce((s, b) => s + (parseFloat(b.stock_quantity) || 0), 0);

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="master-part-form">
      <datalist id="existing-brand-names">
        {brandNameOptions.map((bn) => <option key={bn} value={bn} />)}
      </datalist>
      {draftRestored && <DraftRestoredBar onDiscard={discardDraft} onDismiss={dismissBanner} />}

      {/* Vehicle Compatibility — moved to top, compulsory unless "Fits All Makes" */}
      <Card data-testid="part-compatibility-section">
        <CardContent className="pt-5 space-y-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
            <Car className="h-4 w-4" /> Vehicle Compatibility *
          </div>
          <div className="flex flex-wrap gap-5">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={fitsAllMakes}
                onCheckedChange={handleFitsAllMakesChange}
                data-testid="fits-all-makes-checkbox"
              />
              Fits All Makes (universal part, e.g. engine oil)
            </label>
            <label className={`flex items-center gap-2 text-sm cursor-pointer ${!selectedMake || fitsAllMakes ? 'opacity-50' : ''}`}>
              <Checkbox
                checked={fitsAllModels}
                onCheckedChange={handleFitsAllModelsChange}
                disabled={!selectedMake || fitsAllMakes}
                data-testid="fits-all-models-checkbox"
              />
              Fits All Models of this Make
            </label>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Make {!fitsAllMakes && '*'}</Label>
              <Select value={selectedMake} onValueChange={handleMakeChange} disabled={makesLoading || fitsAllMakes}>
                <SelectTrigger data-testid="compat-make-select"><SelectValue placeholder={fitsAllMakes ? 'All makes' : (makesLoading ? 'Loading...' : 'Select make')} /></SelectTrigger>
                <SelectContent>
                  {availableMakes.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Model</Label>
              <Select value={selectedModel} onValueChange={handleModelChange} disabled={!selectedMake || modelsLoading || fitsAllMakes || fitsAllModels}>
                <SelectTrigger data-testid="compat-model-select"><SelectValue placeholder={fitsAllModels ? 'All models' : (!selectedMake ? 'Pick make first' : (modelsLoading ? 'Loading...' : 'Select model'))} /></SelectTrigger>
                <SelectContent>
                  {availableModels.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Series</Label>
              <Select value={selectedSeries} onValueChange={handleSeriesChange} disabled={!selectedModel || seriesLoading || fitsAllMakes || fitsAllModels}>
                <SelectTrigger data-testid="compat-series-select"><SelectValue placeholder={!selectedModel ? 'Pick model first' : (seriesLoading ? 'Loading...' : 'Select series')} /></SelectTrigger>
                <SelectContent>
                  {availableSeries.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Master Part Details */}
      <Card>
        <CardContent className="pt-5 space-y-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
            <Package className="h-4 w-4" /> Master Part
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="part_name">Master Part Name *</Label>
              <Input
                id="part_name"
                value={master.part_name}
                onChange={(e) => setMaster({ ...master, part_name: e.target.value })}
                placeholder="e.g. Oil Filter"
                data-testid="master-part-name-input"
              />
            </div>
            <div>
              <Label htmlFor="category">Category</Label>
              <Select value={master.category} onValueChange={(v) => setMaster({ ...master, category: v })}>
                <SelectTrigger data-testid="master-part-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>{c.replace('_', ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={master.description}
              onChange={(e) => setMaster({ ...master, description: e.target.value })}
              rows={2}
              placeholder="Optional description shown internally"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="hsn_code">HSN Code (GST)</Label>
              <Input
                id="hsn_code"
                value={master.hsn_code}
                onChange={(e) => setMaster({ ...master, hsn_code: e.target.value })}
                placeholder="e.g. 8708"
                data-testid="master-part-hsn-input"
              />
            </div>
            <div>
              <Label htmlFor="gst_rate">GST Rate (%)</Label>
              <Select value={master.gst_rate} onValueChange={(v) => setMaster({ ...master, gst_rate: v })}>
                <SelectTrigger data-testid="master-part-gst-rate"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0% (Exempt)</SelectItem>
                  <SelectItem value="5">5%</SelectItem>
                  <SelectItem value="12">12%</SelectItem>
                  <SelectItem value="18">18%</SelectItem>
                  <SelectItem value="28">28%</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Brands */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
            <Tag className="h-4 w-4" /> Brands ({brands.length}) · Total stock: {totalStock}
          </div>
          <Button type="button" variant="outline" size="sm" onClick={addBrand} data-testid="add-brand-btn">
            <Plus className="h-4 w-4 mr-1" /> Add Brand
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Each brand keeps its own part number, supplier, prices and stock. On quotations/invoices only the Master Part Name is shown.
        </p>

        {brands.map((b, idx) => (
          <Card key={idx} className="border-l-4 border-l-blue-400" data-testid={`brand-card-${idx}`}>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-600">Brand #{idx + 1}</span>
                {brands.length > 1 && (
                  <Button
                    type="button" variant="ghost" size="sm"
                    className="text-red-600 hover:text-red-700 h-7"
                    onClick={() => removeBrand(idx)}
                    data-testid={`remove-brand-${idx}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs">Brand Name *</Label>
                  <Input
                    value={b.brand_name}
                    onChange={(e) => updateBrand(idx, 'brand_name', e.target.value)}
                    placeholder="Choose or type e.g. Bosch"
                    list="existing-brand-names"
                    data-testid={`brand-name-${idx}`}
                  />
                </div>
                <div>
                  <Label className="text-xs">Part Number</Label>
                  <Input
                    value={b.part_number}
                    onChange={(e) => updateBrand(idx, 'part_number', e.target.value)}
                    placeholder="e.g. BF-100"
                    data-testid={`brand-partno-${idx}`}
                  />
                </div>
                <div>
                  <Label className="text-xs">Supplier</Label>
                  <Select value={b.supplier || ''} onValueChange={(v) => handleSupplierChange(idx, v)}>
                    <SelectTrigger data-testid={`brand-supplier-${idx}`}>
                      <SelectValue placeholder="Select supplier" />
                    </SelectTrigger>
                    <SelectContent>
                      {suppliers.map((s) => (
                        <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Location</Label>
                  <Input
                    value={b.location}
                    onChange={(e) => updateBrand(idx, 'location', e.target.value)}
                    placeholder="Shelf / bin"
                  />
                </div>
                <div>
                  <Label className="text-xs">Purchase Price (₹)</Label>
                  <Input
                    type="number" min="0" step="0.01"
                    value={b.cost_price}
                    onChange={(e) => updateBrand(idx, 'cost_price', e.target.value)}
                    data-testid={`brand-cost-${idx}`}
                  />
                </div>
                <div>
                  <Label className="text-xs">Selling Price (₹)</Label>
                  <Input
                    type="number" min="0" step="0.01"
                    value={b.selling_price}
                    onChange={(e) => updateBrand(idx, 'selling_price', e.target.value)}
                    data-testid={`brand-selling-${idx}`}
                  />
                </div>
                <div>
                  <Label className="text-xs">Stock</Label>
                  <Input
                    type="number" min="0" step="0.01"
                    value={b.stock_quantity}
                    onChange={(e) => updateBrand(idx, 'stock_quantity', e.target.value)}
                    data-testid={`brand-stock-${idx}`}
                  />
                </div>
                <div>
                  <Label className="text-xs">Min Stock</Label>
                  <Input
                    type="number" min="0" step="0.01"
                    value={b.minimum_stock}
                    onChange={(e) => updateBrand(idx, 'minimum_stock', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t">
        <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>Cancel</Button>
        <Button type="submit" disabled={loading} data-testid="save-master-part-btn">
          {loading ? 'Saving...' : isEdit ? 'Update Part' : 'Create Part'}
        </Button>
      </div>
    </form>
  );
};

export default MasterPartForm;
